import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ButtonVariants } from '@carboledger/ui';
import { PageHeader } from '@/components/layout';
import { AddAttachmentModal } from '@/components/common';
import { ATTACHMENT_TARGETS } from '@/lib/api/attachmentsApi';
import { useTranslations, useAssistantFilters } from '@/hooks';
import { ROUTES, MODULE_CERTIFICATE_TYPES } from '@/app/routes';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import {
  useGetAllocationDeclarationQuery,
  useBulkGenerateAllocationDeclarationsMutation,
} from '../api/allocationsApi';
import { ALLOCATION_ACTION_VISIBILITY, hiddenUnless } from '../constants/allocationActions';
import { ALLOCATION_CONFIRM } from '../constants/allocationConfirm';
import { AllocationConfirmDialog } from '../components/AllocationConfirmDialog';
import { GenerateDeclarationModal } from '../components/GenerateDeclarationModal';
import { buildEditDeclarationValues } from '../constants/editDeclarationValues';
import {
  useDeclarationPdf,
  useSaveDeclaration,
  useDownloadDeclaration,
  useDownloadRowDeclaration,
  useUploadDeclarationFile,
  saveRenderedDeclaration,
  useSaveShipmentDetails,
} from '../hooks';
import { UpdateShipmentDetailsModal } from '../components/UpdateShipmentDetailsModal';
import { LinkShipmentsForAllocation } from '../components/LinkShipmentsForAllocation';
import { EMPTY_LIST } from '@/lib/utils';
import styles from './AllocationsPage.module.scss';
import { AllocationsTable } from '../components/AllocationsTable';

// Table sizes this from every rowAction label, but `hidden` means at most two show.
const ACTIONS_WIDTH = 358;

// Outbounds ▸ Allocations.
export const AllocationsPage = () => {
  const t = useTranslations();
  const assistantFilters = useAssistantFilters();
  const navigate = useNavigate();
  const { module } = useParams();

  // `certificateType`, not `module`: the list is GraphQL, and the module scopes it as data.
  const [bulkGenerateDeclarations] = useBulkGenerateAllocationDeclarationsMutation();

  const handleOpen = useCallback(
    (row) => navigate(ROUTES.allocationDetail(module, row.id)),
    [navigate, module],
  );

  // One row or a ticked selection — the preview modal takes the same list either way.
  const [declarationRows, setDeclarationRows] = useState(EMPTY_LIST);
  const handleGenerateDeclaration = useCallback((row) => setDeclarationRows([row]), []);
  const handleBulkGenerateDeclarations = useCallback((rows) => setDeclarationRows(rows), []);
  const handleCloseDeclaration = useCallback(() => setDeclarationRows(EMPTY_LIST), []);

  // Both wizards act on one row, so the open row IS the state.
  const [shipmentRow, setShipmentRow] = useState(null);
  const [linkRow, setLinkRow] = useState(null);
  const handleUpdateShipmentDetails = useCallback((row) => setShipmentRow(row), []);
  const handleCloseUpdateShipment = useCallback(() => setShipmentRow(null), []);
  const handleLinkExistingShipment = useCallback((row) => setLinkRow(row), []);
  const handleCloseLink = useCallback(() => setLinkRow(null), []);

  // Edit, reject and revoke each ask first; the open row rides along until the answer.
  const [confirm, setConfirm] = useState(null);

  const goToEditDeclaration = useCallback(
    (row) => navigate(ROUTES.allocationEditDeclaration(module, row.id)),
    [navigate, module],
  );

  // Editing is a one-shot, so both entry points warn first.
  const handleEditDeclaration = useCallback(
    (row) => setConfirm({ kind: ALLOCATION_CONFIRM.edit, row }),
    [],
  );

  // The preview's button carries a click event, not a row, so it reads the row it has open.
  const handleEditFromPreview = useCallback(() => {
    const row = declarationRows[0];
    if (!row) return;
    // Only the one edit after generating warns first; before that it is just filling the form in.
    if (ALLOCATION_ACTION_VISIBILITY.editDeclaration(row)) handleEditDeclaration(row);
    else goToEditDeclaration(row);
  }, [declarationRows, handleEditDeclaration, goToEditDeclaration]);
  // Filling in a not-yet-generated declaration is not the one-shot edit, so it skips the warning.
  const handleCompleteFromPreview = useCallback(() => {
    const row = declarationRows[0];
    if (row) goToEditDeclaration(row);
  }, [declarationRows, goToEditDeclaration]);
  const canEditFromPreview = useMemo(
    () =>
      declarationRows.length === 1 &&
      ALLOCATION_ACTION_VISIBILITY.editFromPreview(declarationRows[0]),
    [declarationRows],
  );

  const handleRevoke = useCallback(
    (row) => setConfirm({ kind: ALLOCATION_CONFIRM.revoke, row }),
    [],
  );
  const handleMarkRejected = useCallback(
    (row) => setConfirm({ kind: ALLOCATION_CONFIRM.reject, row }),
    [],
  );
  const handleCloseConfirm = useCallback(() => setConfirm(null), []);

  const { save: saveShipmentDetails, isSaving: isSavingShipments } = useSaveShipmentDetails(module);
  const handleSaveShipmentDetails = useCallback(
    async (shipments) => {
      if (await saveShipmentDetails(shipmentRow, shipments)) setShipmentRow(null);
    },
    [saveShipmentDetails, shipmentRow],
  );
  // TODO: no route links an existing shipment to an allocation yet.
  const handleSaveLink = useCallback(() => {}, []);
  const handleLinkSearchChange = useCallback(() => {}, []);
  const [attachmentRow, setAttachmentRow] = useState(null);
  const handleAddAttachments = useCallback((row) => setAttachmentRow(row), []);
  const handleCloseAttachment = useCallback(() => setAttachmentRow(null), []);
  // A list row carries no PoS fields, and a bulk selection agrees on them, so: the first row's.
  const { data: declaration, isLoading: isDeclarationLoading } = useGetAllocationDeclarationQuery(
    { module, id: declarationRows[0]?.id },
    { skip: !declarationRows.length },
  );

  // The same document the preview shows, so what downloads is what was on screen.
  const declarationValues = useMemo(
    () => buildEditDeclarationValues(declarationRows[0], declaration),
    [declarationRows, declaration],
  );
  // Opening the preview only reads and renders; both writes wait for Confirm.
  const { previewBlob } = useDeclarationPdf({
    storedFileId: declaration?.declarationFileId,
    module,
    allocationId: declarationRows[0]?.id,
    values: declarationValues,
    // Rendering before the first read lands would upload a PDF missing every PoS field.
    enabled: declarationRows.length > 0 && !isDeclarationLoading,
  });
  const { save } = useSaveDeclaration({ module, allocationId: declarationRows[0]?.id });
  const uploadDeclarationFile = useUploadDeclarationFile(module);
  const downloadDeclaration = useDownloadDeclaration(module);
  // The row action carries its own row; the preview's button uses the row it opened on.
  const handleDownloadRow = useDownloadRowDeclaration(module);
  const handleDownloadDeclaration = useCallback(
    () => downloadDeclaration(declarationRows[0]?.id, declarationValues, previewBlob),
    [downloadDeclaration, declarationRows, declarationValues, previewBlob],
  );

  // "Confirm declaration & Download": upload the document, save it, then hand it over. Bulk has no file.
  const handleConfirmDeclaration = useCallback(async () => {
    const ids = declarationRows.map((row) => row.id);
    if (ids.length === 0) return;
    if (ids.length > 1) {
      const bulk = await bulkGenerateDeclarations({ module, transactionIds: ids });
      if (bulk.error) return;
      setDeclarationRows(EMPTY_LIST);
      return;
    }

    const uploadedFileId = await uploadDeclarationFile(ids[0], previewBlob);
    const result = await save(
      { ...declarationValues, siteId: declarationRows[0]?.siteId },
      uploadedFileId,
    );
    if (result.error) return;
    // Close first: the download is the last step and nothing on screen waits for it.
    setDeclarationRows(EMPTY_LIST);
    saveRenderedDeclaration(previewBlob, declarationValues);
  }, [
    declarationRows,
    bulkGenerateDeclarations,
    uploadDeclarationFile,
    previewBlob,
    save,
    declarationValues,
    module,
  ]);
  const handleViewDeclaration = useCallback((row) => setDeclarationRows([row]), []);

  const rowActions = useMemo(
    () => [
      {
        id: 'generate-declaration',
        label: t('ALLOCATIONS_ACTION_GENERATE_DECLARATION'),
        variant: ButtonVariants.tertiary,
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.generateDeclaration),
        onClick: handleGenerateDeclaration,
      },
      {
        id: 'download-declaration',
        label: t('ALLOCATIONS_ACTION_DOWNLOAD_DECLARATION'),
        variant: ButtonVariants.tertiary,
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.downloadDeclaration),
        onClick: handleDownloadRow,
      },
      {
        id: 'edit-declaration',
        label: t('ALLOCATIONS_ACTION_EDIT_DECLARATION'),
        variant: ButtonVariants.tertiary,
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.editDeclaration),
        onClick: handleEditDeclaration,
      },
      {
        id: 'update-shipment-details',
        label: t('ALLOCATIONS_ACTION_UPDATE_SHIPMENT_DETAILS'),
        variant: ButtonVariants.tertiary,
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.updateShipmentDetails),
        onClick: handleUpdateShipmentDetails,
      },
      {
        id: 'link-existing-shipment',
        label: t('ALLOCATIONS_ACTION_LINK_EXISTING_SHIPMENT'),
        variant: ButtonVariants.tertiary,
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.linkExistingShipment),
        onClick: handleLinkExistingShipment,
      },
      {
        id: 'add-attachments',
        label: t('ACTION_ADD_ATTACHMENTS'),
        variant: ButtonVariants.tertiary,
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.addAttachments),
        onClick: handleAddAttachments,
      },
    ],
    [
      t,
      handleGenerateDeclaration,
      handleDownloadRow,
      handleEditDeclaration,
      handleUpdateShipmentDetails,
      handleLinkExistingShipment,
      handleAddAttachments,
    ],
  );

  const rowMenuActions = useMemo(
    () => [
      {
        id: 'view-declaration',
        label: t('ALLOCATIONS_ACTION_VIEW_DECLARATION'),
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.viewDeclaration),
        onClick: handleViewDeclaration,
      },
      {
        id: 'mark-rejected',
        label: t('ALLOCATIONS_ACTION_MARK_REJECTED'),
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.markRejected),
        onClick: handleMarkRejected,
      },
      {
        id: 'add-attachments-menu',
        label: t('ACTION_ADD_ATTACHMENTS'),
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.addAttachmentsMenu),
        onClick: handleAddAttachments,
      },
      {
        id: 'revoke',
        label: t('ALLOCATIONS_ACTION_REVOKE'),
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.revoke),
        onClick: handleRevoke,
      },
    ],
    [t, handleViewDeclaration, handleMarkRejected, handleAddAttachments, handleRevoke],
  );

  const bulkActions = useMemo(
    () => [
      {
        id: 'bulk-generate-declarations',
        label: t('ALLOCATIONS_BULK_GENERATE_DECLARATIONS'),
        onClick: handleBulkGenerateDeclarations,
      },
    ],
    [t, handleBulkGenerateDeclarations],
  );

  // Rows are created by fulfilling an order or a shipment, so the empty state has no CTA.
  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('ALLOCATIONS_EMPTY_TITLE'),
      description: t('ALLOCATIONS_EMPTY_DESCRIPTION'),
    }),
    [t],
  );

  const noResults = useMemo(
    () => ({
      title: t('ALLOCATIONS_NO_RESULTS_TITLE'),
      description: t('ALLOCATIONS_NO_RESULTS_DESCRIPTION'),
    }),
    [t],
  );

  return (
    <div className={styles.page}>
      <PageHeader title={t('ALLOCATIONS_TITLE')} />
      <AllocationsTable
        extraFilters={assistantFilters}
        certificateType={MODULE_CERTIFICATE_TYPES[module]}
        emptyState={emptyState}
        noResults={noResults}
        onOpen={handleOpen}
        onViewDeclaration={handleViewDeclaration}
        actionsWidth={ACTIONS_WIDTH}
        rowActions={rowActions}
        rowMenuActions={rowMenuActions}
        bulkActions={bulkActions}
      />

      <UpdateShipmentDetailsModal
        open={shipmentRow != null}
        onClose={handleCloseUpdateShipment}
        allocation={shipmentRow}
        isSaving={isSavingShipments}
        onSubmit={handleSaveShipmentDetails}
      />

      <LinkShipmentsForAllocation
        open={linkRow != null}
        onClose={handleCloseLink}
        allocation={linkRow}
        onSearchChange={handleLinkSearchChange}
        onSave={handleSaveLink}
      />

      <AllocationConfirmDialog
        module={module}
        confirm={confirm}
        onClose={handleCloseConfirm}
        onEdit={goToEditDeclaration}
      />

      <GenerateDeclarationModal
        open={declarationRows.length > 0}
        onClose={handleCloseDeclaration}
        allocations={declarationRows}
        file={previewBlob}
        canEdit={canEditFromPreview}
        onEdit={handleEditFromPreview}
        onComplete={handleCompleteFromPreview}
        onDownload={handleDownloadDeclaration}
        onConfirm={handleConfirmDeclaration}
      />

      <AddAttachmentModal
        open={attachmentRow != null}
        onClose={handleCloseAttachment}
        title={t('ALLOCATIONS_ADD_ATTACHMENT_MODAL_TITLE', {
          transactionId: attachmentRow?.transactionId,
        })}
        target={ATTACHMENT_TARGETS.allocation}
        module={module}
        entityId={attachmentRow?.id}
      />
    </div>
  );
};
